/**
 * Data Plane API — unified asset catalog + mount ledger.
 *
 * The "Data Plane" is one row per uploaded artifact (rules.md, reference PDF,
 * supervisor correction, …). Mounts attach an asset to an
 * ``org / tenant / network / pool / [agent]`` partition so the same blob can
 * be reused across surfaces without re-vectorisation. See backend
 * ``api.rest.data_plane`` and ``models.data_plane`` for the wire shapes.
 *
 *   POST   /data-plane/uploads                          → presigned PUT URL
 *   POST   /data-plane/assets                           → register asset (idempotent on contentHash)
 *   GET    /data-plane/assets                           → list catalog (optional ``includeMounts=true``)
 *   GET    /data-plane/assets/{assetId}                 → asset + mounts
 *   PATCH  /data-plane/assets/{assetId}                 → toggle usage (Off / Rules / Reference); optional body ``networkId`` scopes the change to non-ORG mounts for that net only when the asset is reused across nets
 *   DELETE /data-plane/assets/{assetId}                 → purge asset + mounts
 *   GET    /data-plane/assets/{assetId}/mounts          → mounts for one asset
 *   POST   /data-plane/assets/{assetId}/mounts          → attach asset to a target
 *   DELETE /data-plane/assets/{assetId}/mounts          → detach (target in body)
 *   GET    /data-plane/networks/{id}/mounts             → assets visible to a network
 *
 * All routes are gated by Cognito JWT (TokenAuthorizer); writes additionally
 * require ``BUILD_ROLES`` server-side. Front-end never bypasses RBAC — the
 * UI surfaces the 403 verbatim so reviewers can repro permissions issues.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function encodePath(s) {
  return encodeURIComponent(String(s || '').trim());
}

function parseSizeBytes(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/**
 * GET/POST bodies use ``size`` (``Asset.to_api``). Some UI reads ``sizeBytes``.
 *
 * @param {Record<string, unknown>|null|undefined} asset
 */
export function normalizeAssetFromApi(asset) {
  if (!asset || typeof asset !== 'object') return asset;
  const fromSize = parseSizeBytes(asset.size);
  const fromBytes = parseSizeBytes(asset.sizeBytes);
  const n = fromBytes ?? fromSize;
  if (n === undefined) return { ...asset };
  return { ...asset, size: n, sizeBytes: n };
}

function normalizeNetworkMountsPayload(data) {
  if (!data || typeof data !== 'object') return data;
  const out = { ...data };
  if (out.assets && typeof out.assets === 'object' && !Array.isArray(out.assets)) {
    const next = {};
    for (const [k, v] of Object.entries(out.assets)) {
      next[k] = normalizeAssetFromApi(v);
    }
    out.assets = next;
  }
  const mapMounts = (arr) =>
    Array.isArray(arr)
      ? arr.map((m) => {
          if (!m || typeof m !== 'object') return m;
          const row = { ...m };
          if (row.asset) row.asset = normalizeAssetFromApi(row.asset);
          return row;
        })
      : arr;
  out.networkMounts = mapMounts(out.networkMounts);
  out.orgMounts = mapMounts(out.orgMounts);
  return out;
}

/**
 * @param {string} path
 * @param {RequestInit} [init]
 */
async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || data.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Step 1 of the upload flow — ask the server for a presigned PUT URL on the
 * canonical ``tenants/{TN-…}/data-plane/...`` key. The server short-circuits
 * to an existing ``assetId`` when ``contentHash`` is already known, so the
 * follow-up ``createAsset`` call is a free no-op for duplicates.
 *
 * @param {{
 *   fileName: string,
 *   contentType?: string,
 *   contentHash?: string,    // sha256:hex — compute with computeFileSha256
 *   size?: number,
 * }} body
 * @returns {Promise<{
 *   uploadUrl: string, method: 'PUT', bucket: string, s3Key: string,
 *   contentType: string, expiresIn: number, assetId: string,
 *   fileName: string, contentHash: string|null, deduped: boolean, tenantId: string,
 * }>}
 */
export async function createUploadUrl(body) {
  if (!body || !body.fileName) throw new Error('body.fileName is required');
  return authedFetch('/data-plane/uploads', {
    method: 'POST',
    body: JSON.stringify({
      fileName: body.fileName,
      contentType: body.contentType,
      contentHash: body.contentHash,
      size: body.size,
    }),
  });
}

/**
 * Step 2 — PUT the file bytes to the presigned URL. Returns the raw Response
 * so callers can inspect ``ok`` / ``status`` directly. Note: this fetch does
 * **not** include our REST API bearer — the URL itself is the credential.
 *
 * The server-side presign stamps ``x-amz-meta-data-plane-{content-hash,asset-id}``
 * onto the canonical object so the Phase 4.2 OrgHashIndex can dedupe Titan
 * embedding by reading metadata via HeadObject. Boto3 SigV4 signs those
 * along with ``Content-Type`` when both are set on the Params, which means
 * the browser PUT must echo them as headers — otherwise the signature won't
 * match and S3 returns 403. Pass ``meta`` alongside ``contentType`` to
 * round-trip them; they are optional for callers that don't need dedupe.
 */
export async function uploadFileToPresignedUrl(uploadUrl, file, contentType, meta = null) {
  const headers = {};
  if (contentType) headers['Content-Type'] = contentType;
  if (meta && typeof meta === 'object') {
    for (const [k, v] of Object.entries(meta)) {
      if (v == null) continue;
      headers[`x-amz-meta-${k}`] = String(v);
    }
  }
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers,
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Upload failed (${res.status}): ${text || res.statusText}`);
  }
  return res;
}

/**
 * Step 3 — register the asset row + initial mounts. Idempotent on
 * ``contentHash``: a re-register with the same hash returns the existing
 * ``assetId`` and merges any new mounts.
 *
 * @param {{
 *   fileName: string,
 *   contentHash: string,
 *   size: number,
 *   usageType?: 'RULES'|'REFERENCE'|'OFF',
 *   s3Key?: string,
 *   contentType?: string,
 *   description?: string,
 *   mounts?: import('./dataPlaneTypes').MountTarget[],
 * }} body
 */
export async function createAsset(body) {
  if (!body || !body.fileName) throw new Error('body.fileName is required');
  if (!body.contentHash) throw new Error('body.contentHash is required');
  const usage = body.usageType != null && String(body.usageType).trim() !== ''
    ? body.usageType
    : 'OFF';
  const data = await authedFetch('/data-plane/assets', {
    method: 'POST',
    body: JSON.stringify({ ...body, usageType: usage }),
  });
  if (data && typeof data === 'object' && data.asset) {
    return { ...data, asset: normalizeAssetFromApi(data.asset) };
  }
  return data;
}

/**
 * @param {{ tenantId?: string, networkId?: string, usageType?: string, q?: string, includeMounts?: boolean }} [filter]
 */
export async function listAssets(filter = {}) {
  const params = new URLSearchParams();
  if (filter.networkId) params.set('networkId', filter.networkId);
  if (filter.usageType) params.set('usageType', filter.usageType);
  if (filter.q) params.set('q', filter.q);
  if (filter.includeMounts) params.set('includeMounts', 'true');
  const qs = params.toString();
  const data = await authedFetch(`/data-plane/assets${qs ? `?${qs}` : ''}`);
  if (data && typeof data === 'object' && Array.isArray(data.assets)) {
    return { ...data, assets: data.assets.map((a) => normalizeAssetFromApi(a)) };
  }
  return data;
}

export async function getAsset(assetId) {
  if (!assetId) throw new Error('assetId is required');
  const data = await authedFetch(`/data-plane/assets/${encodePath(assetId)}`);
  if (data && typeof data === 'object' && data.asset) {
    return { ...data, asset: normalizeAssetFromApi(data.asset) };
  }
  return data;
}

/**
 * PATCH /data-plane/assets/{assetId} — toggle Off / Rules / Reference (parity with Run Data memory slider).
 *
 * @param {string} assetId
 * @param {'RULES'|'REFERENCE'|'OFF'} usageType
 * @param {{ networkId?: string, lane?: 'rules'|'reference'|'RULES'|'REFERENCE',
 *   restrictPoolId?: string,
 *   restrictMountScopes?: ('POOL'|'AGENT_POOL')[],
 *   restrictAgentPoolAgents?: string[],
 * }} [opts]
 *   - ``networkId``: when set, only mounts for this network (non-ORG) change mode — other networks stay intact.
 *   - ``lane``: when set, only mounts whose **current** ``usageType`` matches the lane change — keeps Rules / Reference subscriptions independent on the same asset.
 *   - ``restrictPoolId`` / ``restrictMountScopes`` / ``restrictAgentPoolAgents``: agentic pool hold — limit which mount scopes update (server-side).
 */
export async function patchAssetUsage(assetId, usageType, opts = {}) {
  if (!assetId) throw new Error('assetId is required');
  if (!usageType) throw new Error('usageType is required');
  const body = { usageType };
  const nid = opts?.networkId != null && String(opts.networkId).trim() !== '' ? String(opts.networkId).trim() : '';
  if (nid) body.networkId = nid;
  const laneRaw = opts?.lane != null ? String(opts.lane).trim().toUpperCase() : '';
  if (laneRaw === 'RULES' || laneRaw === 'REFERENCE') body.lane = laneRaw;
  const rpid = opts?.restrictPoolId != null && String(opts.restrictPoolId).trim() !== '' ? String(opts.restrictPoolId).trim() : '';
  if (rpid) body.restrictPoolId = rpid;
  if (Array.isArray(opts?.restrictMountScopes) && opts.restrictMountScopes.length > 0) {
    body.restrictMountScopes = opts.restrictMountScopes.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean);
  }
  if (Array.isArray(opts?.restrictAgentPoolAgents) && opts.restrictAgentPoolAgents.length > 0) {
    body.restrictAgentPoolAgents = opts.restrictAgentPoolAgents.map((s) => String(s || '').trim()).filter(Boolean);
  }
  const data = await authedFetch(`/data-plane/assets/${encodePath(assetId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (data && typeof data === 'object' && data.asset) {
    return { ...data, asset: normalizeAssetFromApi(data.asset) };
  }
  return data;
}

export async function deleteAsset(assetId) {
  if (!assetId) throw new Error('assetId is required');
  return authedFetch(`/data-plane/assets/${encodePath(assetId)}`, {
    method: 'DELETE',
  });
}

export async function listAssetMounts(assetId) {
  if (!assetId) throw new Error('assetId is required');
  return authedFetch(`/data-plane/assets/${encodePath(assetId)}/mounts`);
}

/**
 * Attach an asset to a partition. ``target`` shape mirrors the backend
 * ``MountTarget`` dataclass:
 *
 *   { scope: 'ORG' }
 *   { scope: 'NETWORK', networkId }
 *   { scope: 'POOL',    networkId, poolId }
 *   { scope: 'AGENT',   networkId, agentName }
 *   { scope: 'AGENT_POOL', networkId, agentName, poolId }
 *
 * @param {string} assetId
 * @param {object} target
 * @param {{ usageType?: 'RULES'|'REFERENCE'|'OFF' }} [opts] When set, stored on the mount row (defaults to the catalog asset's usageType on the server).
 */
export async function addAssetMount(assetId, target, opts = {}) {
  if (!assetId) throw new Error('assetId is required');
  if (!target || !target.scope) throw new Error('target.scope is required');
  const body = { target };
  const ut = opts?.usageType != null && String(opts.usageType).trim() !== '' ? String(opts.usageType).trim() : '';
  if (ut) body.usageType = ut;
  return authedFetch(`/data-plane/assets/${encodePath(assetId)}/mounts`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function removeAssetMount(assetId, target) {
  if (!assetId) throw new Error('assetId is required');
  if (!target || !target.scope) throw new Error('target.scope is required');
  return authedFetch(`/data-plane/assets/${encodePath(assetId)}/mounts`, {
    method: 'DELETE',
    body: JSON.stringify({ target }),
  });
}

/**
 * Read-only rollup for the "Run -> Input Data" / Data Plane network tab —
 * every asset visible to a network (its own mounts plus inherited org-wide
 * mounts).
 *
 * @param {string} networkId
 * @param {{ includeOrg?: boolean }} [opts]
 */
export async function listNetworkMounts(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.includeOrg === false) params.set('includeOrg', 'false');
  const qs = params.toString();
  const data = await authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/mounts${qs ? `?${qs}` : ''}`,
  );
  return normalizeNetworkMountsPayload(data);
}

/**
 * Generic "✨ Improve with AI" for free-form Context text (the "Write / paste"
 * composer). STATELESS — the server persists nothing; the caller shows the
 * suggestion as accept/reject. Metered under the shared 'refinement' billing
 * row (same as the skill refiner). ``intent`` is an optional hint
 * (``'rules'`` | ``'reference'``) so the model tunes for concision vs. coverage.
 *
 * @param {{ text: string, intent?: 'rules'|'reference'|null }} body
 * @returns {Promise<{
 *   improved: boolean,
 *   original?: string,
 *   suggestion?: { text: string, why: string },
 *   message?: string,
 * }>}
 */
export async function refineText(body) {
  const text = String(body?.text || '').trim();
  if (!text) throw new Error('text is required');
  return authedFetch('/tools/refine', {
    method: 'POST',
    body: JSON.stringify({ text, intent: body?.intent || null }),
  });
}

/**
 * Context Preview — the assembled context **exactly as the LLM receives it** for
 * a given (network, pool, agent). RULES come from the SAME merge the worker runs
 * (faithful, no drift); REFERENCE is shown as placeholders (retrieved at runtime).
 *
 * @param {string} networkId
 * @param {{ pool?: string, agent?: string }} [opts]
 * @returns {Promise<{
 *   networkId: string, poolId: string|null, agentId: string|null,
 *   rules: Array<{ scope: string, label: string, filename: string, contentHash: string, text: string }>,
 *   rulesDropped: Array<{ scope: string, label: string, filename: string, reason: string }>,
 *   reference: Array<{ scope: string, label: string, filenames: string[], sampleChunk?: string }>,
 *   budgetChars: number|null, usedChars: number,
 * }>}
 */
export async function getContextPreview(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.pool) params.set('pool', opts.pool);
  if (opts.agent) params.set('agent', opts.agent);
  const qs = params.toString();
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/context-preview${qs ? `?${qs}` : ''}`,
  );
}

/**
 * Run a REAL vector retrieval for the View Context panel — the same
 * ``search_memory`` the runtime uses, so the user sees exactly what this agent
 * would pull for a given prompt (chunk text + relevance score + source file).
 * Blank ``query`` → backend uses a neutral probe (representative sample).
 *
 * @param {string} networkId
 * @param {{ pool?: string, agent?: string, query?: string, limit?: number }} opts
 * @returns {Promise<{hits: Array, query: string, usedNeutralProbe: boolean, error: ?string}>}
 */
export async function runContextRetrieval(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/context-retrieval`,
    {
      method: 'POST',
      body: JSON.stringify({
        pool: opts.pool || undefined,
        agent: opts.agent || undefined,
        query: opts.query || '',
        ...(opts.limit ? { limit: opts.limit } : {}),
      }),
    },
  );
}

/**
 * Room Context status — the whole-room view backed by pool memory (the only
 * context dimension that accumulates across every call in a room). Returns the
 * thread roster, the cached synthesis (or null), and a staleness count.
 *
 * @param {string} networkId
 * @param {{ pool?: string }} opts
 * @returns {Promise<{threads: Array, threadCount: number, summary: ?object, newThreadsSince: number}>}
 */
export async function getRoomContext(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.pool) params.set('pool', opts.pool);
  const qs = params.toString();
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/room-context${qs ? `?${qs}` : ''}`,
  );
}

/**
 * Room Context action: ``estimate`` (FREE — projected points cost, no model
 * call) or ``synthesize`` (SPENDS — one Haiku call, caches the result with its
 * extraction date). The UI always estimates → confirms → synthesizes.
 *
 * @param {string} networkId
 * @param {{ pool?: string, action?: 'estimate'|'synthesize' }} opts
 */
export async function postRoomContext(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/room-context`,
    {
      method: 'POST',
      body: JSON.stringify({
        pool: opts.pool || undefined,
        action: opts.action || 'estimate',
      }),
    },
  );
}

/**
 * Network context (consolidated across all rooms) — the reporting view + the
 * button-state flags. ``eligible`` = network released with rooms; ``stale`` =
 * re-extraction would yield something new.
 *
 * @param {string} networkId
 * @returns {Promise<{eligible:boolean, roomCount:number, summary:?object, stale:boolean}>}
 */
export async function getNetworkContext(networkId) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/network-context`);
}

/**
 * Extract (consolidate + synthesize) the network-wide context on demand. SPENDS
 * one Haiku call — but no-ops (``status: not_stale``) when no room changed since
 * the last extract, unless ``force``. Gated server-side on released-with-rooms.
 *
 * @param {string} networkId
 * @param {{ force?: boolean }} [opts]
 */
export async function extractNetworkContext(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/network-context-extract`,
    { method: 'POST', body: JSON.stringify({ force: !!opts.force }) },
  );
}

/**
 * Prompt capture (doc/arch/CONTEXT.md §13b) — fetch the fully-assembled prompt
 * an agent was given for a run, for the room's "Context" link. Breaks the
 * black-box effect (see exactly what the agent was told: identity, team
 * composition, role contract, tool specs, injected memory/room-context) and
 * helps fine-tune prompts. Read-only + RBAC-gated server-side.
 *
 * Worker source is keyed by (pool, agent, task); builder source by (run, agent).
 * Resolves to ``null`` on 404 (no capture yet — run hasn't reached
 * prompt-assembly, capture was best-effort-skipped, or it aged out at 30 days).
 *
 * @param {string} networkId
 * @param {{ source?: 'worker'|'builder', agent: string, pool?: string, task?: string, run?: string }} opts
 * @returns {Promise<{source:string, capture:object}|null>}
 */
export async function getPromptCapture(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  if (!opts.agent) throw new Error('agent is required');
  const params = new URLSearchParams();
  params.set('source', opts.source || 'worker');
  params.set('agent', opts.agent);
  if (opts.pool) params.set('pool', opts.pool);
  if (opts.task) params.set('task', opts.task);
  if (opts.run) params.set('run', opts.run);
  try {
    return await authedFetch(
      `/data-plane/networks/${encodePath(networkId)}/prompt-capture?${params.toString()}`,
    );
  } catch (err) {
    // 404 = no capture for this run yet; treat as a soft "nothing to show".
    if (err && (err.status === 404 || /not found/i.test(String(err.message || '')))) {
      return null;
    }
    throw err;
  }
}

export const _internal = { authedFetch, encodePath, API_BASE_URL };
